



import { ClientSettings, VentMonitorModel, VentSettingModel } from "@/models";
import _auth from "@/utils/auth";
import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";

const dangerClass = "text-danger";
const warningClass = "text-warning";

@Component
export default class VentValue extends Vue {
  // Use a class property for dependencies so unit tests can override them
  auth = _auth;

  @Prop() readonly monitor?: { [field in keyof VentMonitorModel]: string };
  @Prop() readonly settings?: { [field in keyof VentSettingModel]: string };
  @Prop() readonly field!: keyof VentMonitorModel | keyof VentSettingModel;

  config: ClientSettings | null = null;

  async created() {
    // The clients settings won't change while on this page
    this.config = await this.auth.getClientSettings();
  }

  get isMonitor(): boolean {
    return !!this.monitor && Object.keys(this.monitor).indexOf(this.field) >= 0;
  }

  get isSetting(): boolean {
    return !!this.settings && Object.keys(this.settings).indexOf(this.field) >= 0;
  }

  get value() {
    if (this.isMonitor) return this.monitor![this.field as keyof VentMonitorModel];
    else if (this.isSetting) return this.settings![this.field as keyof VentSettingModel];
    else return undefined;
  }

  get identifyingClass(): string {
    const type = this.isMonitor ? "monitor" : this.isSetting ? "setting" : "";
    return `vent-value-${type}-${this.field || ""}`;
  }

  get thresholdClass(): string | null {
    // If settings haven't been loaded yet, no need to check thresholds
    if (!this.config) return null;

    // This component is used to render both Set FiO2/PEEP and Measured FiO2/PEEP, but only the Set FiO2/PEEP should
    // have color coding applied
    if (this.isSetting && this.field === "fiO2") {
      return this.calculateThresholdClass(this.config.fiO2RedThreshold, this.config.fiO2YellowThreshold);
    }
    if (this.isSetting && this.field === "peep") {
      return this.calculateThresholdClass(this.config.peepRedThreshold, this.config.peepYellowThreshold);
    }

    return null;
  }

  calculateThresholdClass(dangerThreshold?: number, warningThershold?: number): string | null {
    // If there isn't a value, no need to check thresholds
    if (!this.value) return null;

    const value = parseInt(this.value);

    if (dangerThreshold && value > dangerThreshold) return dangerClass;
    else if (warningThershold && value > warningThershold) return warningClass;
    else return null;
  }
}
